import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useContent } from '../contexts/ContentContext';
import { getIndex, getYearIndex, MonthData } from '../services/api';
import { attributeRoots, AttributeRoot, illustrationRoots } from '../services/illustrations';
import { illustrationHome, illustrationUrl, imageOrigin, resolveIllustrationRoute } from '../utils/illustrationRoute';
import { useIllustrationIndex } from '../hooks/useIllustrationIndex';
import { ArchiveYear, archiveUrl, useArchiveYears } from '../hooks/useArchiveYears';
import { useVisitorFavorites } from '../hooks/useVisitorFavorites';
import { useTagSearch } from '../contexts/TagSearchContext';
import { resolvePage } from '../pages';
import { Bookmark, ChevronLeft, ChevronRight, Search } from 'lucide-react';

function navigate(href: string) {
    window.history.pushState({}, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
}
type Choice = { label: string; href: string; selected?: boolean; disabled?: boolean };
type TrackBounds = { atStart: boolean; atEnd: boolean };
type StowingLabel = { label: string; left: number; top: number; width: number; height: number; x: number; y: number };

function RootMenu({ choices, enabled }: { choices: Choice[]; enabled: boolean }) {
    const { t } = useContent();
    const [open, setOpen] = useState(false);
    const host = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLAnchorElement>(null);
    const menu = useRef<HTMLDivElement>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const focusOnOpen = useRef(false);
    const menuId = useId();
    const cancelClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
    const openMenu = () => { if (enabled) { cancelClose(); setOpen(true); } };
    const items = () => Array.from(menu.current?.querySelectorAll<HTMLAnchorElement>('a[role="menuitem"]') || []);
    const openWithFocus = () => {
        if (open) items()[0]?.focus();
        else { focusOnOpen.current = true; openMenu(); }
    };
    useLayoutEffect(() => {
        if (open && focusOnOpen.current) { items()[0]?.focus(); focusOnOpen.current = false; }
    }, [open]);
    useEffect(() => {
        if (!open) return;
        const closeOutside = (event: PointerEvent) => { if (!host.current?.contains(event.target as Node)) setOpen(false); };
        document.addEventListener('pointerdown', closeOutside, true);
        return () => document.removeEventListener('pointerdown', closeOutside, true);
    }, [open]);
    useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
    return <div ref={host} className="lab-subheader-home-wrap" data-menu-open={open || undefined}
        onPointerEnter={event => { if (event.pointerType !== 'touch') openMenu(); }}
        onPointerLeave={() => { if (!host.current?.contains(document.activeElement)) { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 120); } }}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}
        onKeyDown={event => {
            if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus(); }
        }}>
        <a ref={trigger} className="lab-subheader-home lab-subheader-top" href={illustrationHome} aria-label={t('illustrations.home')}
            aria-haspopup={enabled ? 'menu' : undefined} aria-expanded={enabled ? open : undefined} aria-controls={open ? menuId : undefined}
            onClick={event => {
                if (enabled && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && window.matchMedia('(hover: none)').matches) {
                    event.preventDefault(); cancelClose(); setOpen(value => !value);
                }
            }}
            onKeyDown={event => { if (enabled && ['ArrowDown', 'ArrowUp', ' '].includes(event.key)) { event.preventDefault(); openWithFocus(); } }}>
            <span className="lab-subheader-home-label"><span>{t('illustrations.homeSubject')}</span><span>{t('illustrations.homeDestination')}</span></span>
            {enabled && <ChevronRight className="lab-subheader-home-chevron" size={20} strokeWidth={2.5} aria-hidden="true" />}
        </a>
        {open && <div ref={menu} id={menuId} className="lab-subheader-menu" role="menu" aria-label={t('illustrations.route')}
            onKeyDown={event => {
                const links = items(), current = links.indexOf(document.activeElement as HTMLAnchorElement);
                const index = event.key === 'ArrowDown' ? (current + 1) % links.length : event.key === 'ArrowUp' ? (current - 1 + links.length) % links.length
                    : event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : -1;
                if (index >= 0) { event.preventDefault(); links[index]?.focus(); }
            }}>
            <a href={illustrationHome} role="menuitem" onClick={() => setOpen(false)}>{t('illustrations.home')}</a>
            {choices.map(choice => choice.disabled ? <span role="menuitem" aria-disabled="true" key={choice.label}>{choice.label}</span>
                : <a role="menuitem" href={choice.href} key={choice.label} aria-current={choice.selected ? 'page' : undefined} onClick={() => setOpen(false)}>{choice.label}</a>)}
        </div>}
    </div>;
}

function scrollBehavior(): ScrollBehavior {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

function trackBounds(track: HTMLDivElement | null): TrackBounds {
    if (!track) return { atStart: true, atEnd: true };
    return {
        atStart: track.scrollLeft <= 1,
        atEnd: track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
    };
}

function revealSelected(track: HTMLDivElement | null) {
    const selected = track?.querySelector<HTMLAnchorElement>('a[aria-current="page"]');
    if (!track || !selected) return;
    const trackRect = track.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    let left = track.scrollLeft;
    if (selectedRect.left < trackRect.left) left -= trackRect.left - selectedRect.left;
    else if (selectedRect.right > trackRect.right) left += selectedRect.right - trackRect.right;
    left = Math.max(0, Math.min(track.scrollWidth - track.clientWidth, left));
    if (Math.abs(left - track.scrollLeft) > 1) track.scrollTo({ left, behavior: scrollBehavior() });
}

function scrollTrack(track: HTMLDivElement | null, direction: -1 | 1) {
    if (!track) return;
    const distance = Math.max(100, track.clientWidth * 0.8);
    const left = Math.max(0, Math.min(track.scrollWidth - track.clientWidth, track.scrollLeft + direction * distance));
    track.scrollTo({ left, behavior: scrollBehavior() });
}

function Choices({ items, label }: { items: Choice[]; label: string }) {
    const { t } = useContent();
    const container = useRef<HTMLDivElement>(null);
    const measurement = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const [count, setCount] = useState(0);
    const signature = JSON.stringify(items.map(item => item.label));
    useLayoutEffect(() => {
        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        if (container.current) observer.observe(container.current);
        return () => observer.disconnect();
    }, []);
    useLayoutEffect(() => {
        let used = 0, fitted = 0;
        const children = Array.from(measurement.current?.children || []);
        const total = children.reduce((sum, child) => sum + child.getBoundingClientRect().width + 8, 0);
        for (const child of children) {
            used += child.getBoundingClientRect().width + 8;
            if (used > width - (total > width ? 100 : 0)) break;
            fitted++;
        }
        setCount(fitted);
    }, [width, signature]);
    const selected = items.find(item => item.selected);
    const visible = items.slice(0, count);
    if (selected && !visible.includes(selected) && visible.length) visible[visible.length - 1] = selected;
    const hidden = items.filter(item => !visible.includes(item));
    return <div ref={container} className="lab-subheader-choices">
        <div ref={measurement} className="lab-subheader-measure" aria-hidden="true">{items.map(item => <span key={item.href}>{item.label}</span>)}</div>
        {visible.map(item => item.disabled ? <span key={item.label} aria-disabled="true">{item.label}</span> : <a key={item.href} href={item.href} aria-current={item.selected ? 'page' : undefined}>{item.label}</a>)}
        {!!hidden.length && <select aria-label={label} value={count ? '' : selected?.href || ''} onChange={event => navigate(event.target.value)}>
            <option value="" disabled>{count ? t('illustrations.more') : label}</option>
            {hidden.map(item => <option key={item.label} value={item.href} disabled={item.disabled}>{item.label}</option>)}
        </select>}
    </div>;
}

function DateMenu({ params, archiveYears }: { params: URLSearchParams; archiveYears: ArchiveYear[] }) {
    const { t } = useContent();
    const year = params.get('year') || String(new Date().getFullYear());
    const month = params.get('month');
    const [years, setYears] = useState<string[]>([]);
    const [months, setMonths] = useState<MonthData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const yearTrack = useRef<HTMLDivElement>(null);
    const monthTrack = useRef<HTMLDivElement>(null);
    const [yearBounds, setYearBounds] = useState<TrackBounds>({ atStart: true, atEnd: true });
    const [monthBounds, setMonthBounds] = useState<TrackBounds>({ atStart: true, atEnd: true });
    const routeSearch = params.toString();
    const updateYearBounds = useCallback(() => {
        const next = trackBounds(yearTrack.current);
        setYearBounds(current => current.atStart === next.atStart && current.atEnd === next.atEnd ? current : next);
    }, []);
    const updateMonthBounds = useCallback(() => {
        const next = trackBounds(monthTrack.current);
        setMonthBounds(current => current.atStart === next.atStart && current.atEnd === next.atEnd ? current : next);
    }, []);
    useEffect(() => {
        let active = true;
        setLoading(true); setError(false); setMonths([]);
        getIndex().then(async index => {
            const candidates = Array.from(new Set([String(new Date().getFullYear()), year, ...index.years.map(item => item.year)])).sort((a, b) => b.localeCompare(a));
            const data = index.years.some(item => item.year === year) ? await getYearIndex(year) : { months: [] };
            if (!active) return;
            const available = [...data.months].sort((a, b) => b.month.localeCompare(a.month));
            setYears(candidates); setMonths(available);
            if (!month && available.length && new URLSearchParams(window.location.search).get('view') !== 'image') {
                const next = new URLSearchParams(routeSearch);
                next.set('year', year); next.set('month', available[0].month); next.delete('pageNumber');
                window.history.replaceState(window.history.state, '', '/?' + next);
                window.dispatchEvent(new PopStateEvent('popstate'));
            }
        }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
        // Month changes do not change the public year index.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, attempt]);
    useLayoutEffect(() => {
        revealSelected(yearTrack.current);
        revealSelected(monthTrack.current);
        updateYearBounds();
        updateMonthBounds();
    }, [year, month, years, months, loading, error, updateYearBounds, updateMonthBounds]);
    useEffect(() => {
        const observer = new ResizeObserver(() => {
            updateYearBounds();
            updateMonthBounds();
        });
        if (yearTrack.current) observer.observe(yearTrack.current);
        if (monthTrack.current) observer.observe(monthTrack.current);
        return () => observer.disconnect();
    }, [loading, error, years.length, months.length, updateYearBounds, updateMonthBounds]);
    const monthHref = (value: string) => '/?' + new URLSearchParams({ page: 'gallery', root: 'recent', year, month: value });
    return <div className="lab-subheader-dates">
        <span className="lab-label">{t('illustrations.year')}</span>
        <div className="lab-date-window lab-subheader-years" role="group" aria-label={t('illustrations.year')}>
            <button type="button" aria-label={t('illustrations.nextYears')} disabled={yearBounds.atStart} onClick={() => scrollTrack(yearTrack.current, -1)}><ChevronLeft aria-hidden="true" /></button>
            <div ref={yearTrack} className="lab-date-track" onScroll={updateYearBounds}>
                {years.map(value => <a href={archiveYears.find(item => item.year === value)?.href || illustrationUrl('recent', value)} key={value} aria-current={year === value ? 'page' : undefined}>{value}</a>)}
            </div>
            <button type="button" aria-label={t('illustrations.previousYears')} disabled={yearBounds.atEnd} onClick={() => scrollTrack(yearTrack.current, 1)}><ChevronRight aria-hidden="true" /></button>
        </div>
        <div className="lab-date-window lab-subheader-months" role="group" aria-label={t('tagSearch.dateTitle')}>
            {!loading && !error && !!months.length && <button type="button" aria-label={t('illustrations.nextMonths')} disabled={monthBounds.atStart} onClick={() => scrollTrack(monthTrack.current, -1)}><ChevronLeft aria-hidden="true" /></button>}
            {loading ? <span role="status">{t('common.loading')}</span> : error ? <button type="button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button>
                : !months.length ? <span role="status">{t('illustrations.noMonths')}</span>
                : <div ref={monthTrack} className="lab-date-track" onScroll={updateMonthBounds}>
                    {months.map(item => <a href={monthHref(item.month)} key={item.month} aria-current={month === item.month ? 'page' : undefined}>{t('illustrations.month', { month: Number(item.month) })}</a>)}
                </div>}
            {!loading && !error && !!months.length && <button type="button" aria-label={t('illustrations.previousMonths')} disabled={monthBounds.atEnd} onClick={() => scrollTrack(monthTrack.current, 1)}><ChevronRight aria-hidden="true" /></button>}
        </div>
    </div>;
}

function AttributeMenu({ root, params }: { root: AttributeRoot; params: URLSearchParams }) {
    const { t } = useContent();
    const { data, error, retry } = useIllustrationIndex();
    const values = data?.groups[root]?.values || [];
    const selected = params.get(root);
    if (error) return <button onClick={retry}>{t('common.retry')}</button>;
    if (!data) return <span role="status">{t('common.loading')}</span>;
    return <>
        {selected && !values.some(item => item.slug === selected) && <span role="status">{t('illustrations.missing')}</span>}
        <Choices label={t(`illustrations.${root}`)} items={[
            { href: illustrationUrl(root), label: t('illustrations.overview', { label: t(`illustrations.${root}`) }), selected: !selected },
            ...values.map(item => ({ href: illustrationUrl(root, item.slug, params), label: item.label, selected: selected === item.slug }))
        ]} />
        {!values.length && <span role="status">{t('illustrations.noValues')}</span>}
    </>;
}

function SavedMenu({ params }: { params: URLSearchParams }) {
    const { t } = useContent();
    const { data, error } = useVisitorFavorites();
    const collection = params.get('collection'), unfiled = params.get('unfiled') === '1';
    if (error) return null;
    return <>
        {collection && !data.collections.some(item => item.id === collection) && <span role="status">{t('illustrations.missing')}</span>}
        <Choices label={t('illustrations.favorites')} items={[
            { href: illustrationUrl('favorites'), label: t('illustrations.all'), selected: !collection && !unfiled },
            { href: illustrationUrl('favorites', 'unfiled'), label: t('illustrations.unfiled'), selected: unfiled },
            ...data.collections.map(item => ({ href: illustrationUrl('favorites', item.id), label: item.name, selected: item.id === collection }))
        ]} />
    </>;
}

export default function IllustrationSubheader() {
    const { t } = useContent();
    const { openTagSearch } = useTagSearch();
    let params = new URLSearchParams(window.location.search);
    const origin = imageOrigin(params);
    if (origin) params = new URLSearchParams(origin.slice(2).split('#')[0]);
    const root = resolveIllustrationRoute(window.location.search);
    const visible = resolvePage(window.location.search).definition.kind === 'images';
    const archive = useArchiveYears(visible);
    const archiveRoute = root === 'recent' && params.has('year');
    const archiveHref = archiveRoute && params.get('month') ? archiveUrl(params.get('year')!, params.get('month')!) : archive.latestHref || '';
    const rootChoices = illustrationRoots.filter(value => value !== 'favorites' || root === 'favorites').flatMap(value => {
        const choice = { label: t(`illustrations.${value}`), href: illustrationUrl(value), disabled: false, selected: !archiveRoute && root === value };
        return value === 'character' ? [{ label: t('illustrations.byYear'), href: archiveHref, disabled: !archiveHref, selected: archiveRoute }, choice] : [choice];
    });
    const overview = root === 'home' || root === 'popular' || (root === 'recent' && !params.has('year') && !params.has('month'));
    const compactRoot = archiveRoute || root === 'rating' || (root && attributeRoots.includes(root as AttributeRoot));
    const selectedRoot = archiveRoute ? 'year' : root;
    const bar = useRef<HTMLElement>(null);
    const previousRoot = useRef(selectedRoot);
    const rootBounds = useRef<StowingLabel[]>([]);
    const [stowing, setStowing] = useState<StowingLabel[]>([]);
    const captureRootButtons = useCallback(() => {
        const menu = bar.current?.querySelector('.lab-subheader-menu');
        const overviewButtons = bar.current?.querySelector('.lab-subheader-context[data-overview] .lab-subheader-choices');
        const source = menu || overviewButtons;
        if (!source) return;
        rootBounds.current = Array.from(source.querySelectorAll<HTMLElement>(':scope > a, :scope > span[aria-disabled]'))
            .filter(element => element.getAttribute('href') !== illustrationHome)
            .map(element => {
                const bounds = element.getBoundingClientRect();
                return { label: element.textContent || '', left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height, x: 0, y: 0 };
            }).filter(bounds => bounds.width > 0 && bounds.height > 0);
    }, []);
    useLayoutEffect(() => {
        if (previousRoot.current === selectedRoot) return;
        previousRoot.current = selectedRoot;
        const target = bar.current?.querySelector('.lab-subheader-home')?.getBoundingClientRect();
        if (!visible || !compactRoot || !target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setStowing([]); return; }
        const selected = rootChoices.find(choice => choice.selected)?.label;
        setStowing(rootBounds.current.filter(bounds => bounds.label !== selected).map(bounds => ({ ...bounds,
            x: target.left + target.width / 2 - bounds.left - bounds.width / 2,
            y: target.top + target.height / 2 - bounds.top - bounds.height / 2
        })));
        // Root changes drive the transition; archive loading and language changes do not replay it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoot, visible, compactRoot]);
    useEffect(() => {
        if (!stowing.length) return;
        const timer = setTimeout(() => setStowing([]), 420);
        return () => clearTimeout(timer);
    }, [stowing]);
    useLayoutEffect(() => {
        if (!visible || !overview) return;
        captureRootButtons();
        const observer = new ResizeObserver(captureRootButtons);
        const context = bar.current?.querySelector('.lab-subheader-context');
        if (context) observer.observe(context);
        document.addEventListener('scroll', captureRootButtons, true);
        return () => { observer.disconnect(); document.removeEventListener('scroll', captureRootButtons, true); };
    }, [visible, overview, captureRootButtons]);
    useLayoutEffect(() => {
        const update = () => document.documentElement.style.setProperty('--lab-subheader-height', `${bar.current?.offsetHeight || 0}px`);
        const observer = new ResizeObserver(update);
        if (bar.current) observer.observe(bar.current);
        update();
        return () => { observer.disconnect(); document.documentElement.style.setProperty('--lab-subheader-height', '0px'); };
    }, [visible]);
    if (!visible) return null;
    return <nav ref={bar} className="lab-subheader" aria-label={t('illustrations.route')} data-root={root || 'other'}
        onClickCapture={captureRootButtons} onChangeCapture={captureRootButtons}>
        <RootMenu key={window.location.search} choices={rootChoices} enabled={!overview} />
        {archiveRoute && <span className="lab-subheader-root">{t('illustrations.byYear')}</span>}
        {!!stowing.length && <div className="lab-subheader-stowing" aria-hidden="true">{stowing.map((item, index) => <span key={`${selectedRoot}-${index}`} style={{
            left: item.left, top: item.top, width: item.width, height: item.height,
            '--lab-stow-x': `${item.x}px`, '--lab-stow-y': `${item.y}px`
        } as React.CSSProperties}>{item.label}</span>)}</div>}
        <div className="lab-subheader-context" data-overview={overview || undefined}>
            {overview ? <><Choices label={t('illustrations.route')} items={rootChoices.filter(choice => choice.href !== illustrationUrl('favorites'))} />{archive.error && <button type="button" onClick={archive.retry}>{t('common.retry')}</button>}</>
                : root === 'recent' ? <DateMenu params={params} archiveYears={archive.years} />
                : root && attributeRoots.includes(root as AttributeRoot) ? <AttributeMenu key={root} root={root as AttributeRoot} params={params} />
                : root === 'rating' ? <Choices label={t('illustrations.rating')} items={[5,4,3].map(value => ({ label: `★${value}`, href: illustrationUrl('rating', String(value), params), selected: params.get('rating') === String(value) }))} />
                : root === 'favorites' ? <SavedMenu params={params} /> : null}
        </div>
        <div className="lab-subheader-search"><a href={illustrationUrl('favorites')} aria-current={root === 'favorites' ? 'page' : undefined}><Bookmark aria-hidden="true" />{t('illustrations.favorites')}</a><button className="lab-button" onClick={openTagSearch}><Search aria-hidden="true" />{t('illustrations.search')}</button></div>
    </nav>;
}
